/**
 * needs-payment-on-screen0.ts — F1d A8 screen-0 gate helper.
 *
 * Returns true if any Rule in logic:
 *   1. Contains a PAYMENT_METHOD-source Condition (at any nesting depth)
 *   AND
 *   2. Is scoped to the first screen (screen 0), meaning:
 *      - trigger_scope === 'element' AND scope_ref ∈ screen0.blocks[].id
 *      - trigger_scope === 'screen'  AND scope_ref === screen0.id
 *
 * Used as the await-gate in PublicQuiz / PublicPaywall:
 *   if needsPaymentOnScreen0 → await probePaymentMethod before first
 *   visibility eval (prevents flash of wrong visibility state on screen 0).
 *
 * Pure function: no DOM/React/navigator imports.
 */

import type { Logic } from '@/logic-engine';

/** Minimal screen-0 shape needed by the helper. */
export interface Screen0Shape {
  id: string;
  blocks: Array<{ id: string }>;
}

/** Recursively check condition_group.items for PAYMENT_METHOD source. */
function groupHasPaymentSource(items: unknown[]): boolean {
  for (const item of items) {
    const it = item as Record<string, unknown>;
    if ('source' in it) {
      if (it['source'] === 'PAYMENT_METHOD') return true;
    } else if ('combinator' in it && Array.isArray(it['items'])) {
      // Nested Group — combinator is 'ALL' | 'ANY' (rule.schema.ts).
      if (groupHasPaymentSource(it['items'] as unknown[])) return true;
    }
  }
  return false;
}

/**
 * needsPaymentOnScreen0 — A8 gate check.
 *
 * @param logic    Full logic array from quiz/paywall data.
 * @param screen0  The first screen (index 0) of the funnel.
 * @returns true if a PAYMENT_METHOD condition is tied to screen 0.
 */
export function needsPaymentOnScreen0(logic: Logic, screen0: Screen0Shape): boolean {
  if (!logic.length) return false;

  const screen0BlockIds = new Set(screen0.blocks.map((b) => b.id));

  for (const rule of logic) {
    if (!groupHasPaymentSource(rule.condition_group.items as unknown[])) continue;

    const onScreen0 =
      (rule.trigger_scope === 'element' && screen0BlockIds.has(rule.scope_ref)) ||
      (rule.trigger_scope === 'screen' && rule.scope_ref === screen0.id);

    if (onScreen0) return true;
  }

  return false;
}
